import React, {useRef, useEffect} from 'react'
import {useLocation} from 'react-router'
import clsx from 'clsx'
import {checkIsActive, KTSVG, WithChildren} from '../../../helpers'

type Props = {
  to: string
  title: string
  icon?: string
  fontIcon?: string
  menuTrigger?: 'click' | `{default:'click', lg: 'hover'}`
  menuPlacement?: 'right-start' | 'bottom-start' | 'left-start'
  hasArrow?: boolean
  hasBullet?: boolean
  isMega?: boolean
  megaClassName?: string
  keepOpenOnInsideClick?: boolean
  menuOverflow?: boolean
  badgeCount?: number
  badgeColor?: string
  badgePlacement?: 'after-title' | 'after-arrow' // Optional placement control
}

const MenuInnerWithSub: React.FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  menuTrigger,
  menuPlacement,
  hasArrow = false,
  hasBullet = false,
  isMega = false,
  megaClassName,
  keepOpenOnInsideClick = false,
  menuOverflow = false,
  badgeCount = 0,
  badgeColor = 'primary',
  badgePlacement = 'after-title',
}) => {
  const menuItemRef = useRef<HTMLDivElement>(null)
  const {pathname} = useLocation()

  useEffect(() => {
    if (!menuItemRef.current) {
      return
    }

    if (menuTrigger) {
      menuItemRef.current.setAttribute('data-kt-menu-trigger', menuTrigger)
    }

    if (menuPlacement) {
      menuItemRef.current.setAttribute('data-kt-menu-placement', menuPlacement)
    }

    if (menuOverflow) {
      menuItemRef.current.setAttribute('data-kt-menu-overflow', 'true')
    } else {
      menuItemRef.current.removeAttribute('data-kt-menu-overflow')
    }
  }, [menuTrigger, menuPlacement, menuOverflow])

  return (
    <div ref={menuItemRef} className='menu-item menu-lg-down-accordion me-lg-1'>
      <span
        className={clsx('menu-link py-3', {
          active: checkIsActive(pathname, to),
        })}
      >
        {hasBullet && (
          <span className='menu-bullet'>
            <span className='bullet bullet-dot'></span>
          </span>
        )}

        {icon && (
          <span className='menu-icon'>
            <KTSVG path={icon} className='svg-icon-2' />
          </span>
        )}

        {fontIcon && (
          <span className='menu-icon'>
            <i className={clsx('bi fs-3', fontIcon)}></i>
          </span>
        )}

        <span className='menu-title'>{title}</span>

        {/* Badge placed after title */}
        {badgeCount > 0 && badgePlacement === 'after-title' && (
          <span className={`ms-2 badge badge-${badgeColor} badge-pill`}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}

        {/* Badge placed after arrow (if arrow exists) */}
        {badgeCount > 0 && badgePlacement === 'after-arrow' && (
          <span className={`ms-2 badge badge-${badgeColor} badge-pill`}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}

        {hasArrow && <span className='menu-arrow'></span>}
      </span>
      <div
        className={clsx(
          'menu-sub menu-sub-lg-down-accordion menu-sub-lg-dropdown',
          isMega
            ? megaClassName || 'w-100 w-lg-600px p-5 p-lg-5'
            : 'menu-rounded-0 py-lg-4 w-lg-225px'
        )}
        {...(!keepOpenOnInsideClick ? {'data-kt-menu-dismiss': 'true'} : {})}
      >
        {children}
      </div>
    </div>
  )
}

export {MenuInnerWithSub}